import { z } from "zod/v4";

// =============================================================================
// Structural type for JS injection mode-like objects
// =============================================================================

type JsInjectionModeLike = {
  type: "immediately" | "onWindowLoad" | "timeout";
  timeout?: number | string;
};

// =============================================================================
// Structural type for onsite injection-like objects
// =============================================================================

type OnsiteInjectionLike = {
  customCss?: string | null;
  customJs?: string | null;
};

// =============================================================================
// Refinement Functions (exported for testing/selective use)
// =============================================================================

export const jsInjectionModeRefinements = {
  timeoutRequiredWhenTypeIsTimeout: <T extends JsInjectionModeLike>(
    mode: T
  ) => {
    if (mode.type === "timeout") {
      const timeout =
        typeof mode.timeout === "string" ? Number(mode.timeout) : mode.timeout;
      return (
        timeout !== undefined &&
        timeout !== null &&
        !isNaN(timeout) &&
        timeout >= 0
      );
    }
    return true;
  },
};

export const jsInjectionModeRefinementMessages = {
  timeoutRequiredWhenTypeIsTimeout:
    "Timeout value is required and must be non-negative when type is 'timeout'",
};

export const onsiteInjectionRefinements = {};

export const onsiteInjectionRefinementMessages = {
  requiresCustomCssOrCustomJs:
    "At least one of customCss or customJs must be non-empty",
};

// =============================================================================
// Helper to apply all standard JS injection mode refinements
// =============================================================================

export function applyJsInjectionModeRefinements<
  S extends z.ZodObject<z.ZodRawShape>,
>(schema: S) {
  return schema.refine(
    (mode) => {
      const m = mode as JsInjectionModeLike;
      return jsInjectionModeRefinements.timeoutRequiredWhenTypeIsTimeout(m);
    },
    {
      error: jsInjectionModeRefinementMessages.timeoutRequiredWhenTypeIsTimeout,
    }
  );
}

// =============================================================================
// Helper to apply onsite injection refinements
// =============================================================================

export function applyOnsiteInjectionRefinements<
  S extends z.ZodType<OnsiteInjectionLike>,
>(schema: S) {
  return schema;
}
